<template>
	<div class="p-fluid p-component p-formgrid p-grid p-input-filled">
		<div class="p-field p-col-12">
			<label for="id">{{$t('Squeeze.Login.Login')}} *</label>
			<Dropdown
				id="id"
				v-model="value.id"
				:options="allUsers"
				@change="update"
				:loading="loading"
				optionValue="id"
				:optionLabel="getColumnLabel"
			>
			</Dropdown>
			<small v-if="v$.id.$invalid && showErrorMessage" class="p-error">
				{{ $t('Forms.Val.MissingRequired', { field: $t('Squeeze.Login.Login')}) }}
			</small>
		</div>
	</div>
</template>

<script lang="ts">
import {defineComponent, nextTick, onMounted, PropType, reactive, ref, watch} from "vue";
import {User} from "@dex/squeeze-client-ts";
import {required} from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";
import {useI18n} from "vue-i18n";
import {useToast} from "primevue/usetoast";
import Dropdown from "primevue/dropdown";
import {FormState} from "@/util/Types";

type UserFormState = FormState<User, "id">;

export default defineComponent({
	name: "UserManagementForm",
	components: {
		Dropdown,
	},
	props: {
		user: {
			type: Object as PropType<User>,
			required: true,
		},
		allUsers: {
			type: Array as PropType<User[]>,
			required: true,
		},
		showErrorMessage: {
			type: Boolean,
		},
	},
	emits: ['update'],
	setup(props, {emit}) {
		const {t} = useI18n();
		const toast = useToast();

		/** Show loading? */
		const loading = ref(false);

		/** Current Object of all input-fields */
		const value = reactive<UserFormState>({id: undefined});

		/** Determines the required rules for validation */
		const rules = {
			id: { required },
		}

		/** Use Vuelidate */
		const v$ = useVuelidate(rules, value);

		onMounted(async () => {
			Object.assign(value, props.user);

			// Emit Validated-Values onMounted. Otherwise there can be validation-errors when editing multiple entries
			await v$.value.$validate();
			emit("update", value, v$.value.$invalid);
		});

		/** Watch prop at set value object, because props are not allowed to be mutated */
		watch(props.user, () => {
			Object.assign(value, props.user);
		})

		/**
		 * Gets the Label for Columns. Is used in the Dropdowns for the Columns
		 * @param column Current-Column
		 */
		const getColumnLabel = (column: User): string => {
			return column.firstName + " " + column.lastName + " (" + column.login + ")";
		}

		/** Triggered on change of any field */
		const update = async () => {
			// need nextTick, because the dropdown don't check the change of a selected element (is to slow)
			await nextTick();
			emit("update", value, v$.value.$invalid);
		}

		return {
			t,
			toast,
			loading,
			value,
			props,
			v$,
			update,
			getColumnLabel,
		}
	},
});

</script>

<style scoped>

</style>
